// src/pages/Inbound/InboundPage.jsx
// Inbound Query Management — log employee-initiated support queries,
// track resolution, handle docket details for Input Items type.
// Route: /inbound

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button, Input, Select, DatePicker, Switch, Tag,
  Spin, Popconfirm, Modal, message as antMsg, Empty, Tooltip, Upload, Table as AntTable, Steps,
} from 'antd';
import {
  PlusOutlined, CheckCircleOutlined, CloseCircleOutlined,
  SearchOutlined, ReloadOutlined, DownloadOutlined,
  InfoCircleOutlined, PhoneIncomingOutlined,
  FileTextOutlined, LockOutlined, UnlockOutlined,
  UploadOutlined, InboxOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { addLog } from '../Admin/SystemLogPage';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as XLSX from 'xlsx';
dayjs.extend(relativeTime);

const { TextArea } = Input;

// ── Design tokens ────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  teal:'#0891b2',  lteal:'#ecfeff',
  purple:'#7c3aed',lpurple:'#f5f3ff',
  orange:'#ea580c',lorange:'#fff7ed',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

// Query type icon map
const TYPE_ICON = {
  ipad_accessories:     '📱',
  company_systems:      '💻',
  expense_reimbursement:'💰',
  mediclaim_policy:     '🏥',
  esim_activation:      '📡',
  input_items:          '📦',
  others:               '❓',
};

// ── Helpers ───────────────────────────────────────────────────────
const fmtDate = d => d ? dayjs(d).format('DD MMM YYYY') : '—';
const StatusTag = ({ closed }) => (
  <Tag style={{
    borderRadius:20, fontWeight:700, fontSize:11, padding:'2px 10px',
    background: closed ? C.lgreen : C.lamber,
    color:       closed ? C.green  : C.amber,
    border:`1px solid ${closed ? '#86efac' : '#fde68a'}`,
  }}>
    {closed ? '✅ Closed' : '⏳ Open'}
  </Tag>
);

// ══════════════════════════════════════════════════════════════════
export default function InboundPage() {
  const [queries,     setQueries]     = useState([]);
  const [queryTypes,  setQueryTypes]  = useState([]);
  const [stats,       setStats]       = useState(null);
  const [employees,   setEmployees]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [total,       setTotal]       = useState(0);

  // Filters
  const [search,      setSearch]      = useState('');
  const [filterType,  setFilterType]  = useState('all');
  const [filterStatus,setFilterStatus]= useState('all'); // all|open|closed
  const [filterFrom,  setFilterFrom]  = useState(null);
  const [filterTo,    setFilterTo]    = useState(null);
  const [page,        setPage]        = useState(1);
  const [activeChip,  setActiveChip]  = useState('all'); // tracks which KPI chip is active
  const PAGE_SIZE = 20;

  // Modals
  const [logModal,    setLogModal]    = useState(false);
  const [closeModal,  setCloseModal]  = useState(null); // query object
  const [detailModal, setDetailModal] = useState(null); // query object
  const [saving,      setSaving]      = useState(false);

  // Log form state
  const [form, setForm] = useState({
    employeeId:'', callDate: dayjs().format('YYYY-MM-DD'),
    queryTypeKey:'', remarks:'',
  });

  // Close form state
  const [closeForm, setCloseForm] = useState({
    closingRemarks:'', closedDate: dayjs().format('YYYY-MM-DD'),
    docketDate:'', docketNo:'', dispatchBy:'',
  });

  // Bulk upload state
  const [bulkModal,      setBulkModal]      = useState(false);
  const [bulkStep,       setBulkStep]       = useState(0); // 0=select+upload, 1=preview, 2=result
  const [bulkQueryType,  setBulkQueryType]  = useState('');
  const [bulkRows,       setBulkRows]       = useState([]);   // parsed rows
  const [bulkResult,     setBulkResult]     = useState(null); // { inserted, errors }
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // ── Fetch query types ──────────────────────────────────────────
  const loadQueryTypes = useCallback(async () => {
    try {
      const res = await api.get('/inbound/query-types');
      setQueryTypes(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch { setQueryTypes([]); }
  }, []);

  // ── Fetch employees (for dropdown) ───────────────────────────
  const loadEmployees = useCallback(async () => {
    try {
      const res = await api.get('/employees?page=1&limit=9999');
      const rows = res.data?.data || res.data || [];
      setEmployees(Array.isArray(rows) ? rows : []);
    } catch { setEmployees([]); }
  }, []);

  // ── Fetch queries (with filters) ──────────────────────────────
  const loadQueries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search)                params.append('search',   search);
      if (filterType !== 'all')  params.append('queryTypeKey', filterType);
      if (filterStatus === 'open')   params.append('isClosed', 'false');
      if (filterStatus === 'closed') params.append('isClosed', 'true');
      if (filterFrom) params.append('fromDate', dayjs(filterFrom).format('YYYY-MM-DD'));
      if (filterTo)   params.append('toDate',   dayjs(filterTo).format('YYYY-MM-DD'));
      params.append('page',  String(page));
      params.append('limit', String(PAGE_SIZE));

      const [qRes, sRes] = await Promise.all([
        api.get(`/inbound?${params}`),
        api.get('/inbound/summary'),
      ]);

      const payload = qRes.data?.data ?? qRes.data ?? [];
      const rows = Array.isArray(payload) ? payload : payload.data || [];
      setQueries(rows);
      setTotal(qRes.data?.total || rows.length);
      setStats(sRes.data?.data ?? sRes.data);
    } catch (e) {
      antMsg.error('Failed to load inbound queries');
    } finally { setLoading(false); }
  }, [search, filterType, filterStatus, filterFrom, filterTo, page]);

  useEffect(() => { loadQueryTypes(); loadEmployees(); }, [loadQueryTypes, loadEmployees]);
  useEffect(() => { loadQueries(); }, [loadQueries]);

  // ── Active query type meta ────────────────────────────────────
  const activeType = queryTypes.find(t => t.key === form.queryTypeKey);
  const closeType  = closeModal ? queryTypes.find(t => t.key === closeModal.queryTypeKey) : null;

  // ── Log new query ─────────────────────────────────────────────
  async function handleLogQuery() {
    if (!form.employeeId)   { antMsg.error('Select an employee'); return; }
    if (!form.queryTypeKey) { antMsg.error('Select a query type'); return; }
    if (!form.callDate)     { antMsg.error('Select a call date'); return; }
    if (activeType?.remarksRequired && !form.remarks.trim()) {
      antMsg.error('Remarks are mandatory for "Others" query type'); return;
    }

    setSaving(true);
    try {
      await api.post('/inbound', {
        employeeId:   +form.employeeId,
        callDate:     form.callDate,
        queryTypeKey: form.queryTypeKey,
        remarks:      form.remarks || undefined,
      });
      antMsg.success('✅ Inbound query logged successfully');
      addLog('UPLOAD', 'SUCCESS', `Inbound query logged — ${activeType?.label}`, {
        employeeId: form.employeeId, type: form.queryTypeKey,
      });
      setLogModal(false);
      setForm({ employeeId:'', callDate: dayjs().format('YYYY-MM-DD'), queryTypeKey:'', remarks:'' });
      loadQueries();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to log query';
      antMsg.error(msg);
      addLog('UPLOAD', 'ERROR', 'Inbound query log failed', { error: msg });
    } finally { setSaving(false); }
  }

  // ── Close query ───────────────────────────────────────────────
  async function handleCloseQuery() {
    if (!closeModal) return;
    // Docket fields are optional — fill in if available

    setSaving(true);
    try {
      await api.patch(`/inbound/${closeModal.id}/close`, {
        closingRemarks: closeForm.closingRemarks || undefined,
        closedDate:     closeForm.closedDate     || dayjs().format('YYYY-MM-DD'),
        docketDate:     closeForm.docketDate     || undefined,
        docketNo:       closeForm.docketNo       || undefined,
        dispatchBy:     closeForm.dispatchBy     || undefined,
      });
      antMsg.success('✅ Query closed successfully');
      addLog('CONFIG', 'SUCCESS', `Inbound query #${closeModal.id} closed`, {
        type: closeModal.queryTypeKey,
        hasDocket: closeType?.hasDocket,
        docketNo: closeForm.docketNo || null,
      });
      setCloseModal(null);
      setCloseForm({ closingRemarks:'', closedDate: dayjs().format('YYYY-MM-DD'), docketDate:'', docketNo:'', dispatchBy:'' });
      loadQueries();
    } catch (e) {
      antMsg.error(e?.response?.data?.message || 'Failed to close query');
    } finally { setSaving(false); }
  }

  // ── Reopen query ──────────────────────────────────────────────
  async function handleReopen(id) {
    try {
      await api.patch(`/inbound/${id}/reopen`);
      antMsg.success('Query reopened');
      addLog('CONFIG', 'WARNING', `Inbound query #${id} reopened`);
      loadQueries();
    } catch { antMsg.error('Failed to reopen'); }
  }

  // ── Export ────────────────────────────────────────────────────
  function exportExcel() {
    const rows = queries.map(q => ({
      'Query ID':        q.id,
      'Call Date':       fmtDate(q.callDate),
      'Emp Code':        q.employee?.empCode || '',
      'Employee':        q.employee?.empName || '',
      'Designation':     q.employee?.designation || '',
      'Division':        q.employee?.division || '',
      'Query Type':      q.queryTypeLabel,
      'Query Description': q.remarks || '',
      'Status':          q.isClosed ? 'Closed' : 'Open',
      'Closing Remarks': q.closingRemarks || '',
      'Closing Date':    q.closedDate ? fmtDate(q.closedDate) : '',
      'Closed At':       q.closedAt ? dayjs(q.closedAt).format('DD MMM YYYY HH:mm') : '',
      'Docket No.':      q.docketNo || '',
      'Dispatch Date':   fmtDate(q.docketDate),
      'Dispatch By':     q.dispatchBy || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inbound Queries');
    XLSX.writeFile(wb, `InboundQueries_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    addLog('EXPORT', 'SUCCESS', `Inbound queries exported — ${rows.length} rows`);
    antMsg.success('Exported!');
  }

  // ── Bulk upload helpers ───────────────────────────────────────
  function downloadBulkTemplate() {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Employee Code', 'Call Date', 'Query Description'],
      ['EMP001', dayjs().format('YYYY-MM-DD'), 'Optional description here'],
      ['EMP002', dayjs().format('YYYY-MM-DD'), ''],
    ]);
    ws['!cols'] = [{ wch: 18 }, { wch: 16 }, { wch: 40 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bulk Upload');
    XLSX.writeFile(wb, 'InboundQuery_BulkTemplate.xlsx');
  }

  function parseBulkFile(file) {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'array', cellDates: true, raw: false });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const raw  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });
        if (raw.length < 2) { antMsg.error('Template is empty — add at least one data row'); return; }

        const header = (raw[0] || []).map(h => (h || '').toString().trim().toLowerCase());
        const codeIdx    = header.findIndex(h => h.includes('employee') || h.includes('code') || h === 'emp code');
        const dateIdx    = header.findIndex(h => h.includes('call date') || h.includes('calldate') || h.includes('date'));
        const remarkIdx  = header.findIndex(h => h.includes('query description') || h.includes('description') || h.includes('remark'));

        if (codeIdx < 0 || dateIdx < 0) {
          antMsg.error('Could not find required columns. Make sure the template has "Employee Code" and "Call Date"');
          return;
        }

        const rows = [];
        for (let i = 1; i < raw.length; i++) {
          const row = raw[i];
          if (!row || !row.some(v => v != null && String(v).trim())) continue; // skip blank rows
          const empCode = row[codeIdx] != null ? String(row[codeIdx]).trim() : '';
          const callDate = row[dateIdx] != null ? (() => {
            const v = row[dateIdx];
            if (v instanceof Date) return dayjs(v).format('YYYY-MM-DD');
            const d = dayjs(String(v).trim());
            return d.isValid() ? d.format('YYYY-MM-DD') : String(v).trim();
          })() : '';
          const remarks = remarkIdx >= 0 && row[remarkIdx] != null ? String(row[remarkIdx]).trim() : '';
          rows.push({ key: i, empCode, callDate, remarks });
        }

        if (!rows.length) { antMsg.error('No data rows found in the file'); return; }
        setBulkRows(rows);
        setBulkStep(1);
      } catch (err) {
        antMsg.error('Failed to parse file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // prevent auto-upload
  }

  async function handleBulkSubmit() {
    if (!bulkQueryType) { antMsg.error('Select a query type first'); return; }
    setBulkSubmitting(true);
    try {
      const res = await api.post('/inbound/bulk-import', {
        queryTypeKey: bulkQueryType,
        rows: bulkRows.map(r => ({ empCode: r.empCode, callDate: r.callDate, remarks: r.remarks || undefined })),
      });
      const result = res.data?.data ?? res.data;
      setBulkResult(result);
      setBulkStep(2);
      if (result.inserted > 0) {
        addLog('UPLOAD', 'SUCCESS', `Inbound bulk import: ${result.inserted} rows`, { type: bulkQueryType });
        loadQueries();
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Bulk import failed';
      antMsg.error(Array.isArray(msg) ? msg.join(', ') : msg, 6);
    } finally { setBulkSubmitting(false); }
  }

  function closeBulkModal() {
    setBulkModal(false);
    setBulkStep(0);
    setBulkQueryType('');
    setBulkRows([]);
    setBulkResult(null);
  }

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <div style={{ background:'#f0f4f8', minHeight:'100vh' }}>

      {/* ── Header ── */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:'20px 28px', boxShadow:'0 4px 20px rgba(27,45,107,0.25)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:'#fff',
              display:'flex', alignItems:'center', gap:10 }}>
              📲 Inbound Query Management
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:3 }}>
              Log, track and resolve employee-initiated support queries
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Button icon={<ReloadOutlined/>} onClick={loadQueries} loading={loading}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff' }}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined/>} onClick={exportExcel}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff' }}>
              Export
            </Button>
            <Button icon={<UploadOutlined/>}
              onClick={() => { setBulkModal(true); setBulkStep(0); }}
              style={{ borderRadius:8, background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)', color:'#fff', fontWeight:600 }}>
              Bulk Upload
            </Button>
            <Button type="primary" icon={<PlusOutlined/>}
              onClick={() => setLogModal(true)}
              style={{ borderRadius:8, background:'#fff', color:C.navy,
                border:'none', fontWeight:700 }}>
              Log Inbound Query
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding:'24px 28px 60px' }}>

        {/* ── Stats KPI chips ── */}
        {(() => {
          const chips = [
            {
              id:'all', icon:'📋', label:'Total Queries',
              value:stats?.total, color:C.navy, bg:'#eff6ff',
              onClick: () => { setFilterStatus('all'); setFilterType('all'); setActiveChip('all'); setPage(1); },
            },
            {
              id:'open', icon:'⏳', label:'Open',
              value:stats?.open, color:C.amber, bg:C.lamber,
              onClick: () => { setFilterStatus('open'); setFilterType('all'); setActiveChip('open'); setPage(1); },
            },
            {
              id:'closed', icon:'✅', label:'Closed',
              value:stats?.closed, color:C.green, bg:C.lgreen,
              onClick: () => { setFilterStatus('closed'); setFilterType('all'); setActiveChip('closed'); setPage(1); },
            },
            ...queryTypes.map(t => ({
              id: t.key,
              icon:  TYPE_ICON[t.key] || '❓',
              label: t.label.length > 22 ? t.label.slice(0,22)+'…' : t.label,
              value: stats?.byType?.find?.(b => b.key === t.key)?.count ?? 0,
              color: C.blue, bg: C.lblue,
              onClick: () => { setFilterType(t.key); setFilterStatus('all'); setActiveChip(t.key); setPage(1); },
            })),
          ];
          return (
            <div style={{ display:'grid',
              gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',
              gap:12, marginBottom:20 }}>
              {chips.map(k => {
                const active = activeChip === k.id;
                return (
                  <div key={k.id} onClick={k.onClick}
                    style={{
                      background:   active ? k.color : k.bg,
                      borderRadius: 14,
                      border:`2px solid ${active ? k.color : k.color+'33'}`,
                      borderTop:`3px solid ${k.color}`,
                      padding:'14px 16px',
                      cursor:'pointer',
                      boxShadow: active
                        ? `0 6px 20px ${k.color}40`
                        : '0 1px 4px rgba(0,0,0,0.04)',
                      transform: active ? 'translateY(-2px)' : '',
                      transition:'all .18s',
                      position:'relative',
                    }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 6px 16px ${k.color}30`; }}}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.04)'; }}}>
                    {/* Active indicator dot */}
                    {active && (
                      <div style={{
                        position:'absolute', top:8, right:8,
                        width:8, height:8, borderRadius:'50%',
                        background:'#fff', opacity:0.85,
                      }}/>
                    )}
                    <div style={{ fontSize:22, marginBottom:4 }}>{k.icon}</div>
                    <div style={{
                      fontSize:24, fontWeight:800, lineHeight:1,
                      color: active ? '#fff' : k.color,
                    }}>
                      {k.value ?? '—'}
                    </div>
                    <div style={{
                      fontSize:10, fontWeight:700,
                      textTransform:'uppercase', letterSpacing:'0.5px', marginTop:4,
                      color: active ? 'rgba(255,255,255,0.85)' : C.text3,
                    }}>
                      {k.label}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ── Filters ── */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16,
          background:C.white, padding:'12px 16px', borderRadius:12,
          border:`1px solid ${C.border}`, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <Input prefix={<SearchOutlined style={{ color:C.text3 }}/>}
            placeholder="Search employee, code, remarks…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            allowClear style={{ width:260, borderRadius:8 }}/>
          <Select value={filterType} onChange={v => { setFilterType(v); setActiveChip(v === 'all' && filterStatus === 'all' ? 'all' : v); setPage(1); }}
            style={{ width:200 }}>
            <Select.Option value="all">All Query Types</Select.Option>
            {queryTypes.map(t => (
              <Select.Option key={t.key} value={t.key}>
                {TYPE_ICON[t.key]} {t.label}
              </Select.Option>
            ))}
          </Select>
          <Select value={filterStatus} onChange={v => { setFilterStatus(v); setActiveChip(v === 'all' && filterType === 'all' ? 'all' : v); setPage(1); }}
            style={{ width:130 }}>
            <Select.Option value="all">All Status</Select.Option>
            <Select.Option value="open">⏳ Open</Select.Option>
            <Select.Option value="closed">✅ Closed</Select.Option>
          </Select>
          <DatePicker placeholder="From date" value={filterFrom}
            onChange={d => { setFilterFrom(d); setPage(1); }}
            style={{ width:140, borderRadius:8 }} format="DD MMM YYYY"/>
          <DatePicker placeholder="To date" value={filterTo}
            onChange={d => { setFilterTo(d); setPage(1); }}
            style={{ width:140, borderRadius:8 }} format="DD MMM YYYY"/>
          <span style={{ fontSize:12, color:C.text3, alignSelf:'center' }}>
            {total} records
          </span>
          {(search||filterType!=='all'||filterStatus!=='all'||filterFrom||filterTo) && (
            <Button size="small" onClick={() => {
              setSearch(''); setFilterType('all'); setFilterStatus('all');
              setFilterFrom(null); setFilterTo(null); setPage(1);
              setActiveChip('all');
            }} style={{ borderRadius:8 }}>Clear</Button>
          )}
        </div>

        {/* ── Query cards ── */}
        {loading
          ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>
          : queries.length === 0
            ? <div style={{ textAlign:'center', padding:60, background:C.white,
                borderRadius:14, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
                <div style={{ color:C.text3, fontSize:14 }}>No inbound queries found</div>
                <Button type="primary" icon={<PlusOutlined/>}
                  onClick={() => setLogModal(true)}
                  style={{ marginTop:16, borderRadius:8, background:C.navy }}>
                  Log First Query
                </Button>
              </div>
            : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {queries.map(q => {
                  const qType = queryTypes.find(t => t.key === q.queryTypeKey) || {};
                  const emp   = q.employee || {};
                  return (
                    <div key={q.id} style={{
                      background:C.white, borderRadius:14,
                      border:`1.5px solid ${q.isClosed ? '#86efac44' : '#fde68a88'}`,
                      borderLeft:`4px solid ${q.isClosed ? C.green : C.amber}`,
                      boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
                      padding:'16px 20px',
                      transition:'box-shadow .15s',
                    }}>
                      <div style={{ display:'flex', justifyContent:'space-between',
                        alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>

                        {/* Left — main info */}
                        <div style={{ flex:1, minWidth:280 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10,
                            marginBottom:8, flexWrap:'wrap' }}>
                            {/* Query type badge */}
                            <span style={{
                              fontSize:12, fontWeight:700, padding:'3px 12px',
                              borderRadius:20, background:C.lblue, color:C.blue,
                              border:`1px solid ${C.blue}33`,
                            }}>
                              {TYPE_ICON[q.queryTypeKey] || '❓'} {q.queryTypeLabel}
                            </span>
                            <StatusTag closed={q.isClosed}/>
                            {qType.hasDocket && q.isClosed && q.docketNo && (
                              <Tag style={{ borderRadius:20, fontSize:10, fontWeight:700,
                                background:'#f0fdf4', color:C.green,
                                border:`1px solid ${C.green}44` }}>
                                📦 Docket: {q.docketNo}
                              </Tag>
                            )}
                          </div>

                          {/* Employee info */}
                          <div style={{ display:'flex', alignItems:'center', gap:12,
                            marginBottom:8 }}>
                            <div style={{ width:36, height:36, borderRadius:'50%',
                              background:`linear-gradient(135deg,${C.blue},${C.navy})`,
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>
                              {(emp.empName||'?').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight:700, fontSize:14, color:C.text1 }}>
                                {emp.empName || '—'}
                              </div>
                              <div style={{ fontSize:11, color:C.text3 }}>
                                {emp.empCode} · {emp.designation} · {emp.division}
                              </div>
                            </div>
                          </div>

                          {/* Remarks */}
                          {q.remarks && (
                            <div style={{ fontSize:12, color:C.text2,
                              background:C.surf, borderRadius:8,
                              padding:'6px 12px', marginBottom:6,
                              border:`1px solid ${C.border}` }}>
                              <span style={{ fontWeight:700, color:C.text3 }}>Description: </span>
                              {q.remarks}
                            </div>
                          )}

                          {/* Closing remarks */}
                          {q.isClosed && q.closingRemarks && (
                            <div style={{ fontSize:12, color:C.green,
                              background:C.lgreen, borderRadius:8,
                              padding:'6px 12px', marginBottom:6,
                              border:`1px solid #86efac44` }}>
                              <span style={{ fontWeight:700 }}>Resolution: </span>
                              {q.closingRemarks}
                            </div>
                          )}

                          {/* Docket details */}
                          {q.isClosed && qType.hasDocket && q.docketNo && (
                            <div style={{ display:'flex', gap:16, fontSize:11,
                              color:C.text3, marginTop:4, flexWrap:'wrap' }}>
                              <span>📦 <strong>Docket No:</strong> {q.docketNo}</span>
                              <span>📅 <strong>Dispatch Date:</strong> {fmtDate(q.docketDate)}</span>
                              <span>🚚 <strong>Dispatch By:</strong> {q.dispatchBy}</span>
                            </div>
                          )}
                        </div>

                        {/* Right — dates + actions */}
                        <div style={{ display:'flex', flexDirection:'column',
                          alignItems:'flex-end', gap:8, minWidth:160 }}>
                          <div style={{ fontSize:11, color:C.text3, textAlign:'right' }}>
                            <div>📅 <strong>Call Date:</strong> {fmtDate(q.callDate)}</div>
                            {q.isClosed && q.closedAt && (
                              <div style={{ marginTop:2 }}>
                                ✅ Closed: {q.closedDate ? fmtDate(q.closedDate) : dayjs(q.closedAt).format('DD MMM YYYY')}
                              </div>
                            )}
                            <div style={{ marginTop:2, fontSize:10 }}>
                              #{q.id} · {dayjs(q.createdAt).fromNow()}
                            </div>
                          </div>

                          <div style={{ display:'flex', gap:6, flexWrap:'wrap',
                            justifyContent:'flex-end' }}>
                            <Button size="small" icon={<InfoCircleOutlined/>}
                              onClick={() => setDetailModal(q)}
                              style={{ borderRadius:8 }}>
                              Detail
                            </Button>
                            {!q.isClosed ? (
                              <Button size="small" type="primary"
                                icon={<CheckCircleOutlined/>}
                                onClick={() => { setCloseModal(q); setCloseForm({closingRemarks:'',docketDate:'',docketNo:'',dispatchBy:''}); }}
                                style={{ borderRadius:8, background:C.green, border:'none' }}>
                                Close Query
                              </Button>
                            ) : (
                              <Popconfirm title="Reopen this query?"
                                onConfirm={() => handleReopen(q.id)}
                                okText="Reopen" okButtonProps={{ style:{background:C.amber} }}>
                                <Button size="small" icon={<UnlockOutlined/>}
                                  style={{ borderRadius:8, color:C.amber,
                                    borderColor:C.amber }}>
                                  Reopen
                                </Button>
                              </Popconfirm>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {total > PAGE_SIZE && (
                  <div style={{ display:'flex', justifyContent:'center',
                    gap:8, paddingTop:16 }}>
                    <Button disabled={page<=1} onClick={() => setPage(p => p-1)}>← Prev</Button>
                    <span style={{ alignSelf:'center', fontSize:12, color:C.text3 }}>
                      Page {page} of {Math.ceil(total/PAGE_SIZE)}
                    </span>
                    <Button disabled={page>=Math.ceil(total/PAGE_SIZE)}
                      onClick={() => setPage(p => p+1)}>Next →</Button>
                  </div>
                )}
              </div>
            )
        }
      </div>

      {/* ══ LOG NEW QUERY MODAL ════════════════════════════════════ */}
      <Modal open={logModal} onCancel={() => setLogModal(false)}
        title={<span>📲 Log Inbound Query</span>}
        footer={null} width={560}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>

          {/* Employee */}
          <div>
            <label style={labelStyle}>Employee <Required/></label>
            <Select showSearch optionFilterProp="children"
              value={form.employeeId || undefined}
              onChange={v => setForm(p => ({ ...p, employeeId: v }))}
              placeholder="Search by name or code…"
              style={{ width:'100%', borderRadius:8 }}
              filterOption={(input, opt) =>
                (opt?.label||'').toLowerCase().includes(input.toLowerCase())}>
              {employees.map(e => (
                <Select.Option key={e.id} value={e.id}
                  label={`${e.empName} ${e.empCode}`}>
                  <div style={{ fontWeight:600 }}>{e.empName}</div>
                  <div style={{ fontSize:11, color:C.text3 }}>
                    {e.empCode} · {e.designation} · {e.division}
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Call date */}
          <div>
            <label style={labelStyle}>Call Date <Required/></label>
            <DatePicker value={form.callDate ? dayjs(form.callDate) : null}
              onChange={d => setForm(p => ({ ...p, callDate: d ? d.format('YYYY-MM-DD') : '' }))}
              style={{ width:'100%', borderRadius:8 }} format="DD MMM YYYY"/>
          </div>

          {/* Query type */}
          <div>
            <label style={labelStyle}>Query Type <Required/></label>
            <Select value={form.queryTypeKey || undefined}
              onChange={v => setForm(p => ({ ...p, queryTypeKey:v, remarks:'' }))}
              placeholder="Select query type…"
              style={{ width:'100%', borderRadius:8 }}>
              {queryTypes.map(t => (
                <Select.Option key={t.key} value={t.key}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:16 }}>{TYPE_ICON[t.key]||'❓'}</span>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{t.label}</div>
                      {t.hasDocket && (
                        <div style={{ fontSize:10, color:C.orange }}>
                          📦 Requires docket details when closing
                        </div>
                      )}
                      {t.remarksRequired && (
                        <div style={{ fontSize:10, color:C.red }}>
                          ✱ Query Description is mandatory
                        </div>
                      )}
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Query Description */}
          <div>
            <label style={labelStyle}>
              Query Description {activeType?.remarksRequired && <Required/>}
            </label>
            <TextArea rows={3} value={form.remarks}
              onChange={e => setForm(p => ({ ...p, remarks:e.target.value }))}
              placeholder={activeType?.remarksRequired
                ? 'Mandatory — describe the query in detail…'
                : 'Optional — describe the query…'}
              style={{ borderRadius:8,
                borderColor: activeType?.remarksRequired && !form.remarks.trim()
                  ? C.red : undefined }}/>
            {activeType?.remarksRequired && !form.remarks.trim() && (
              <div style={{ fontSize:11, color:C.red, marginTop:3 }}>
                Query Description is mandatory for "Others" type
              </div>
            )}
          </div>

          {/* Info banner for docket type */}
          {activeType?.hasDocket && (
            <div style={{ background:C.lorange, border:`1px solid ${C.orange}44`,
              borderRadius:10, padding:'10px 14px',
              fontSize:12, color:C.orange, display:'flex', gap:8 }}>
              <span>📦</span>
              <span>
                This query type requires <strong>docket details</strong> (Dispatch Date,
                Docket No., Dispatch By) when you close the query.
              </span>
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
            <Button onClick={() => setLogModal(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleLogQuery}
              style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
              Log Query
            </Button>
          </div>
        </div>
      </Modal>

      {/* ══ CLOSE QUERY MODAL ═════════════════════════════════════ */}
      <Modal open={!!closeModal} onCancel={() => setCloseModal(null)}
        title={<span>✅ Close Query #{closeModal?.id}</span>}
        footer={null} width={520}>
        {closeModal && (
          <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>
            {/* Summary */}
            <div style={{ background:C.surf, borderRadius:10, padding:'12px 16px',
              border:`1px solid ${C.border}` }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text1, marginBottom:4 }}>
                {TYPE_ICON[closeModal.queryTypeKey]} {closeModal.queryTypeLabel}
              </div>
              <div style={{ fontSize:12, color:C.text3 }}>
                {closeModal.employee?.empName} · {fmtDate(closeModal.callDate)}
              </div>
              {closeModal.remarks && (
                <div style={{ fontSize:12, color:C.text2, marginTop:6 }}>
                  Description: {closeModal.remarks}
                </div>
              )}
            </div>

            {/* Closing date — user picks the actual resolution date */}
            <div>
              <label style={labelStyle}>Closing Date <Required/></label>
              <DatePicker
                value={closeForm.closedDate ? dayjs(closeForm.closedDate) : dayjs()}
                onChange={d => setCloseForm(p => ({ ...p, closedDate: d ? d.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD') }))}
                style={{ width:'100%', borderRadius:8 }}
                format="DD MMM YYYY"
                disabledDate={d => d && d.isAfter(dayjs(), 'day')}
                allowClear={false}
              />
              <div style={{ fontSize:11, color:C.text3, marginTop:3 }}>
                Date when the query was actually resolved
              </div>
            </div>

            {/* Closing remarks */}
            <div>
              <label style={labelStyle}>Closing Remarks / Resolution</label>
              <TextArea rows={3} value={closeForm.closingRemarks}
                onChange={e => setCloseForm(p => ({ ...p, closingRemarks:e.target.value }))}
                placeholder="Describe how the query was resolved…"
                style={{ borderRadius:8 }}/>
            </div>

            {/* Docket details — only for input_items type */}
            {closeType?.hasDocket && (
              <>
                <div style={{
                  background:C.lorange, border:`1px solid ${C.orange}44`,
                  borderRadius:10, padding:'10px 14px',
                  fontSize:12, color:C.orange, fontWeight:600,
                }}>
                  📦 Docket Details Required — items have been dispatched
                </div>

                <div>
                  <label style={labelStyle}>Date of Dispatch</label>
                  <DatePicker value={closeForm.docketDate ? dayjs(closeForm.docketDate) : null}
                    onChange={d => setCloseForm(p => ({ ...p, docketDate: d ? d.format('YYYY-MM-DD') : '' }))}
                    style={{ width:'100%', borderRadius:8 }} format="DD MMM YYYY"/>
                </div>
                <div>
                  <label style={labelStyle}>Docket No.</label>
                  <Input value={closeForm.docketNo}
                    onChange={e => setCloseForm(p => ({ ...p, docketNo:e.target.value }))}
                    placeholder="e.g. BLR-2026-00452"
                    style={{ borderRadius:8 }}/>
                </div>
                <div>
                  <label style={labelStyle}>Dispatch By</label>
                  <Input value={closeForm.dispatchBy}
                    onChange={e => setCloseForm(p => ({ ...p, dispatchBy:e.target.value }))}
                    placeholder="e.g. BlueDart / DTDC / By hand"
                    style={{ borderRadius:8 }}/>
                </div>
              </>
            )}

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 }}>
              <Button onClick={() => setCloseModal(null)}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={handleCloseQuery}
                icon={<CheckCircleOutlined/>}
                style={{ background:C.green, border:'none', borderRadius:8, fontWeight:700 }}>
                Mark as Closed
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ══ BULK UPLOAD MODAL ════════════════════════════════════ */}
      <Modal open={bulkModal} onCancel={closeBulkModal}
        title={<span>📤 Bulk Upload Inbound Queries</span>}
        footer={null} width={680} destroyOnClose>

        {/* Steps indicator */}
        <Steps current={bulkStep} size="small" style={{ marginBottom:24 }}
          items={[
            { title:'Select Type & Upload' },
            { title:'Preview' },
            { title:'Result' },
          ]}/>

        {/* ── STEP 0: Query type selector + file upload ── */}
        {bulkStep === 0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {/* Query type */}
            <div>
              <label style={labelStyle}>Query Type <Required/></label>
              <Select value={bulkQueryType || undefined}
                onChange={v => setBulkQueryType(v)}
                placeholder="Select query type for all rows in this upload…"
                style={{ width:'100%', borderRadius:8 }}>
                {queryTypes.map(t => (
                  <Select.Option key={t.key} value={t.key}>
                    {TYPE_ICON[t.key]||'❓'} {t.label}
                  </Select.Option>
                ))}
              </Select>
              <div style={{ fontSize:11, color:C.text3, marginTop:4 }}>
                All rows in the uploaded file will be logged under this query type.
              </div>
            </div>

            {/* Template download */}
            <div style={{ background:C.lblue, borderRadius:10, padding:'12px 16px',
              border:`1px solid ${C.blue}30`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>📄 Download Template</div>
                <div style={{ fontSize:11, color:C.text2, marginTop:2 }}>
                  Excel with columns: <b>Employee Code</b>, <b>Call Date</b> (YYYY-MM-DD), <b>Query Description</b>
                </div>
              </div>
              <Button size="small" icon={<DownloadOutlined/>} onClick={downloadBulkTemplate}
                style={{ borderRadius:8, borderColor:C.blue, color:C.blue, fontWeight:600 }}>
                Template
              </Button>
            </div>

            {/* Upload area */}
            <Upload.Dragger
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={parseBulkFile}
              disabled={!bulkQueryType}>
              <div style={{ padding:'20px 0' }}>
                <InboxOutlined style={{ fontSize:36, color: bulkQueryType ? C.blue : C.text3 }}/>
                <div style={{ fontSize:14, fontWeight:600, color: bulkQueryType ? C.text1 : C.text3, marginTop:8 }}>
                  {bulkQueryType ? 'Drop your Excel file here, or click to browse' : 'Select a Query Type above to enable upload'}
                </div>
                <div style={{ fontSize:12, color:C.text3, marginTop:4 }}>
                  Supports .xlsx, .xls
                </div>
              </div>
            </Upload.Dragger>
          </div>
        )}

        {/* ── STEP 1: Preview parsed rows ── */}
        {bulkStep === 1 && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ background:C.surf, borderRadius:10, padding:'10px 16px',
              border:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <span style={{ fontWeight:700, color:C.navy }}>
                  {TYPE_ICON[bulkQueryType]||'❓'} {queryTypes.find(t=>t.key===bulkQueryType)?.label}
                </span>
                <span style={{ marginLeft:12, fontSize:12, color:C.text3 }}>
                  {bulkRows.length} row{bulkRows.length !== 1 ? 's' : ''} parsed
                </span>
              </div>
              <Button size="small" onClick={() => setBulkStep(0)}>← Back</Button>
            </div>

            <AntTable
              dataSource={bulkRows}
              rowKey="key"
              size="small"
              pagination={{ pageSize: 10, size:'small', showSizeChanger:false }}
              scroll={{ y: 300 }}
              columns={[
                { title:'#', dataIndex:'key', width:45, render:(_,__,i)=>i+1 },
                { title:'Employee Code', dataIndex:'empCode', width:140,
                  render:v=><b style={{ color:C.navy }}>{v||<span style={{color:C.red}}>EMPTY</span>}</b> },
                { title:'Call Date', dataIndex:'callDate', width:130 },
                { title:'Query Description', dataIndex:'remarks', ellipsis:true,
                  render:v=>v||<span style={{color:C.text3}}>—</span> },
              ]}
            />

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <Button onClick={closeBulkModal}>Cancel</Button>
              <Button type="primary" loading={bulkSubmitting} onClick={handleBulkSubmit}
                icon={<UploadOutlined/>}
                style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
                Submit {bulkRows.length} Row{bulkRows.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Result ── */}
        {bulkStep === 2 && bulkResult && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {/* Summary */}
            <div style={{ display:'flex', gap:12 }}>
              <div style={{ flex:1, background:C.lgreen, borderRadius:12, padding:'16px',
                textAlign:'center', border:`1px solid #86efac` }}>
                <div style={{ fontSize:32, fontWeight:800, color:C.green }}>{bulkResult.inserted}</div>
                <div style={{ fontSize:12, fontWeight:700, color:C.green }}>✅ Inserted Successfully</div>
              </div>
              <div style={{ flex:1, background: bulkResult.errors?.length ? C.lred : C.lgreen,
                borderRadius:12, padding:'16px', textAlign:'center',
                border:`1px solid ${bulkResult.errors?.length ? '#fca5a5' : '#86efac'}` }}>
                <div style={{ fontSize:32, fontWeight:800, color: bulkResult.errors?.length ? C.red : C.green }}>
                  {bulkResult.errors?.length || 0}
                </div>
                <div style={{ fontSize:12, fontWeight:700, color: bulkResult.errors?.length ? C.red : C.green }}>
                  {bulkResult.errors?.length ? '❌ Errors / Skipped' : '✅ No Errors'}
                </div>
              </div>
            </div>

            {/* Error list */}
            {bulkResult.errors?.length > 0 && (
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:C.red, marginBottom:8 }}>
                  Failed Rows — please fix and re-upload:
                </div>
                <AntTable
                  dataSource={bulkResult.errors.map((e,i)=>({...e,key:i}))}
                  size="small"
                  pagination={false}
                  scroll={{ y: 220 }}
                  columns={[
                    { title:'Row', dataIndex:'row', width:55 },
                    { title:'Emp Code', dataIndex:'empCode', width:120 },
                    { title:'Reason', dataIndex:'reason', render:v=><span style={{color:C.red}}>{v}</span> },
                  ]}
                />
              </div>
            )}

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <Button onClick={() => { setBulkStep(0); setBulkRows([]); setBulkResult(null); }}>
                Upload Another
              </Button>
              <Button type="primary" onClick={closeBulkModal}
                style={{ background:C.navy, borderRadius:8, fontWeight:700 }}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ══ DETAIL MODAL ══════════════════════════════════════════ */}
      <Modal open={!!detailModal} onCancel={() => setDetailModal(null)}
        title={<span>📋 Query Detail #{detailModal?.id}</span>}
        footer={<Button onClick={() => setDetailModal(null)}>Close</Button>}
        width={480}>
        {detailModal && (() => {
          const q = detailModal;
          const qt = queryTypes.find(t => t.key === q.queryTypeKey) || {};
          return (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {[
                ['Employee',   `${q.employee?.empName || '—'} (${q.employee?.empCode || ''})`],
                ['Designation',q.employee?.designation || '—'],
                ['Division',   q.employee?.division    || '—'],
                ['Call Date',  fmtDate(q.callDate)],
                ['Query Type', `${TYPE_ICON[q.queryTypeKey]||''} ${q.queryTypeLabel}`],
                ['Status',     q.isClosed ? '✅ Closed' : '⏳ Open'],
                ['Query Description', q.remarks || '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ display:'flex', gap:12 }}>
                  <span style={{ width:110, flexShrink:0, fontSize:12,
                    fontWeight:700, color:C.text3 }}>{label}</span>
                  <span style={{ fontSize:13, color:C.text1 }}>{val}</span>
                </div>
              ))}
              {q.isClosed && <>
                <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:10 }}>
                  {[
                    ['Closing Remarks', q.closingRemarks || '—'],
                    ['Closing Date',    q.closedDate ? fmtDate(q.closedDate) : '—'],
                    ['Closed At',       q.closedAt ? dayjs(q.closedAt).format('DD MMM YYYY HH:mm') : '—'],
                    ['Closed By',       q.closedBy || '—'],
                    ...(qt.hasDocket ? [
                      ['Docket No.',    q.docketNo   || '—'],
                      ['Dispatch Date', fmtDate(q.docketDate)],
                      ['Dispatch By',   q.dispatchBy || '—'],
                    ] : []),
                  ].map(([label, val]) => (
                    <div key={label} style={{ display:'flex', gap:12, marginBottom:8 }}>
                      <span style={{ width:110, flexShrink:0, fontSize:12,
                        fontWeight:700, color:C.text3 }}>{label}</span>
                      <span style={{ fontSize:13, color:C.text1 }}>{val}</span>
                    </div>
                  ))}
                </div>
              </>}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────
const Required = () => <span style={{ color:'#dc2626', marginLeft:3 }}>*</span>;
const labelStyle = {
  display:'block', fontSize:12, fontWeight:700,
  color:'#4a6580', marginBottom:5,
};